import { z } from 'zod';
import {
  CLIENT_REFUSAL,
  CLIENT_SORT_FIELDS,
  DOCTOR_CLINIC_MAX,
  DOCTOR_EMAIL_MAX,
  DOCTOR_NAME_MAX,
  DOCTOR_PHONE_MAX,
  DOCTOR_WEBSITE_MAX,
  MERGE_REFUSAL,
  PATIENT_NAME_MAX,
  tooLongDetail,
  validEmail,
} from '../../src/domain/clients/clients';
import {
  CLIENT_CASE_DUES,
  CLIENT_CASE_SORTS,
  CLIENT_CASE_STATUSES,
  DOCTOR_PATIENT_SORT_FIELDS,
} from '../../src/domain/clients/caseFilter';
import { CONVERSION_OUTCOMES } from '../../src/domain/clients/conversion';
import { DOCTOR_SHOWS, PATIENT_SHOWS } from '../../src/domain/clients/summary';
import { IdSchema, InstantSchema, RowVersionSchema, pageOf } from './common.schema';
import { AddressInputSchema, DoctorAddressSchema } from './geo.schema';
import { JobOrderSchema } from './jobOrder.schema';

export {
  CLIENT_CASE_DUES,
  CLIENT_CASE_SORTS,
  CLIENT_CASE_STATUSES,
  CLIENT_SORT_FIELDS,
  DOCTOR_PATIENT_SORT_FIELDS,
  DOCTOR_SHOWS,
  PATIENT_SHOWS,
};

/**
 * Doctors and patients, each a record with its own profile and its cases. The
 * request of 6 October 2026 (second), items 4 and 5.
 *
 * A job order is linked to one of each, `doctorId` and `patientId`, and keeps
 * its two names as well: the copy every list, search and report reads. Ids are
 * integers in the database and strings here, as everywhere.
 *
 * Changed by the request of 6 October 2026 (third):
 *
 *   item 1  two doctors whose names differ only in a title or in punctuation
 *           are possible duplicates, and one can be merged into the other;
 *   item 2  each record carries an ID made from its number, `code`: `DR-0012`
 *           for a doctor and `PT-00031` for a patient;
 *   item 3  a patient is ONE record whichever doctor they are seen by. The
 *           record names no doctor of its own: `doctors` lists everyone the
 *           patient has a case with, read off the cases;
 *   item 4  changing a name is its own permission.
 *
 * Changed again by the request of 6 October 2026 (fifth):
 *
 *   section 1  each list has a summary of counts, and can be narrowed to the
 *              records one of them counts (`show`);
 *   section 2  a doctor's telephone number is kept as E.164 and the address in
 *              the parts its country's post asks for. What was typed before is
 *              kept beside them (`mobile`, `deliveryAddress`) until management
 *              has read what the conversion would do and run it;
 *   section 4  a doctor's and a patient's cases are searched, narrowed, ordered
 *              and paged by the server, a doctor's patients likewise, and both
 *              can be exported.
 *
 * Reading any of it needs `seeIdentity`, as the two names on a case do. A
 * write needs `seeIdentity` as well, and `manageClients` to add a record, edit
 * a doctor's details or delete a record with no cases; `renameClients` to
 * change a doctor's or a patient's name; `mergeDoctors` to merge. An
 * accountant who holds only Payments, Items and Archive is refused every one
 * of these routes: they belong to none of those three screens.
 *
 * The request schemas below say what a client sends and within which limits.
 * The server reads a body field by field and answers in its own order and
 * words, which `contracts/openapi.ts` writes out beside each route: a doctor
 * is answered with every field that needs attention at once.
 */

const count = z.number().int().nonnegative();

/**
 * A doctor's record: nineteen keys, in this order. `caseCount` counts the
 * doctor's job orders that are not deleted, `activeCaseCount` those of them
 * that are also not dispatched and not archived, and `lastCaseAt` is when the
 * newest of the counted cases was taken in, or null with none.
 *
 * What a screen shows: the telephone number is `phoneDisplay`, else the old
 * `mobile`; the address is `address.lines` when `address` is not null and
 * `addressNeedsReview` is false, else the old `deliveryAddress`.
 */
export const DoctorSchema = z.object({
  id: IdSchema,
  /** The doctor's ID: `DR-` and the id padded to four digits. Never typed, never reused. */
  code: z.string(),
  name: z.string(),
  clinic: z.string().nullable(),
  /** The telephone number as E.164, `+971501234567`, or null. */
  phone: z.string().nullable(),
  /** The same number in libphonenumber's INTERNATIONAL format, `+971 50 123 4567`, or null. */
  phoneDisplay: z.string().nullable(),
  /** The OLD free text, exactly as stored, or null. No longer written. */
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  /** Null, or the address in its eleven keys. See `DoctorAddressSchema`. */
  address: DoctorAddressSchema.nullable(),
  /** The OLD free text, exactly as stored, or null. No longer written. */
  deliveryAddress: z.string().nullable(),
  /** True after the conversion could not read the old `mobile` with certainty. */
  phoneNeedsReview: z.boolean(),
  /** True after the conversion could not place the old `deliveryAddress` with certainty. */
  addressNeedsReview: z.boolean(),
  caseCount: count,
  activeCaseCount: count,
  /** The different patients among the doctor's job orders that are not deleted. */
  patientCount: count,
  lastCaseAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  rowVersion: RowVersionSchema,
});

/**
 * A doctor a patient has been to: who, and how many of that patient's job
 * orders that are not deleted are with that doctor.
 */
export const PatientDoctorSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  clinic: z.string().nullable(),
  caseCount: count,
});

/**
 * A patient's record: ten keys, and no doctor of its own. `doctors` is EVERY
 * doctor the patient has a job order with, the deleted left out, the most
 * cases first, then by name, by character code, then by id; `doctorCount` is
 * how many that is. Counted as a doctor's is.
 */
export const PatientSchema = z.object({
  id: IdSchema,
  /** The patient's ID: `PT-` and the id padded to five digits. */
  code: z.string(),
  name: z.string(),
  doctors: z.array(PatientDoctorSchema),
  doctorCount: count,
  caseCount: count,
  activeCaseCount: count,
  lastCaseAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  rowVersion: RowVersionSchema,
});

const DoctorNameSchema = z
  .string({
    required_error: CLIENT_REFUSAL.doctorNameRequired,
    invalid_type_error: CLIENT_REFUSAL.doctorNameRequired,
  })
  .trim()
  .min(1, CLIENT_REFUSAL.doctorNameRequired)
  .max(DOCTOR_NAME_MAX, tooLongDetail(DOCTOR_NAME_MAX));

const PatientNameSchema = z
  .string({
    required_error: CLIENT_REFUSAL.patientNameRequired,
    invalid_type_error: CLIENT_REFUSAL.patientNameRequired,
  })
  .trim()
  .min(1, CLIENT_REFUSAL.patientNameRequired)
  .max(PATIENT_NAME_MAX, tooLongDetail(PATIENT_NAME_MAX));

/** Free text within its limit, trimmed. Left off, null or empty all mean the record has none. */
const detail = (max: number) => z.string().trim().max(max, tooLongDetail(max)).nullish();

/**
 * `POST /doctors`. Requires `manageClients`. Only the name is required, until
 * an address is sent: an address is all or nothing, and a record with one has
 * a clinic too. An email address is optional and, when given, has one `@` with
 * text on both sides; the website is free text within its limit. `phone` is
 * the number with its country code: once its spaces, hyphens, dots and round
 * brackets are gone it begins with `+` and is a valid number by libphonenumber
 * with no country assumed, and it is kept as E.164. `address` is the whole
 * address in its parts, or null for none. 409 when another doctor already has
 * the name and the clinic, compared trimmed, with inner white space collapsed
 * and without regard to case. The number is stricter than the library: a plus
 * and digits and nothing else once its punctuation is gone, so an extension
 * or letters are refused and never dropped.
 */
export const NewDoctorSchema = z.object({
  name: DoctorNameSchema,
  clinic: detail(DOCTOR_CLINIC_MAX),
  phone: z
    .string()
    .trim()
    .max(DOCTOR_PHONE_MAX, CLIENT_REFUSAL.phoneInvalid)
    .nullish(),
  email: z
    .string()
    .trim()
    .max(DOCTOR_EMAIL_MAX, tooLongDetail(DOCTOR_EMAIL_MAX))
    .refine((value) => value === '' || validEmail(value), CLIENT_REFUSAL.emailInvalid)
    .nullish(),
  website: detail(DOCTOR_WEBSITE_MAX),
  address: AddressInputSchema.nullish(),
});

/**
 * `PATCH /doctors/{id}`, with If-Match. Any of the six fields; send only what
 * changes, and null or an empty string to clear one of the five optional ones
 * (`address` is cleared with null). A name that differs from the one the
 * record has needs `renameClients`, and is written to every job order of the
 * doctor, deleted ones included, in the same save; a name sent unchanged is no
 * rename. Any of the five details sent, changed or not, needs
 * `manageClients`, so a rename by somebody who holds only `renameClients`
 * sends the name alone. `address` is never a part of an address: an object
 * replaces the whole of it.
 */
export const UpdateDoctorSchema = NewDoctorSchema.partial();

/**
 * True to keep a name although another patient already has it: a different
 * person of the same name. Without it that is a 409.
 */
const AllowSameNameSchema = z.boolean().optional();

/**
 * `POST /patients`. Requires `manageClients`. The name is all of a patient:
 * no doctor is sent. 409 when another patient already has the name, unless
 * `allowSameName` is true.
 */
export const NewPatientSchema = z.object({
  name: PatientNameSchema,
  allowSameName: AllowSameNameSchema,
});

/**
 * `PATCH /patients/{id}`, with If-Match. Requires `renameClients`. The name,
 * which is written to every job order of the patient, deleted ones included,
 * in the same save. The same 409 as a new patient, and the same way past it.
 */
export const RenamePatientSchema = z.object({
  name: PatientNameSchema,
  allowSameName: AllowSameNameSchema,
});

/**
 * `POST /doctors/{id}/merge`. Requires `mergeDoctors`. The doctor in the
 * address is the one that is kept; `duplicateId` is the doctor whose job
 * orders move to it and whose record is then removed. It cannot be undone.
 */
export const MergeDoctorSchema = z.object({
  duplicateId: z
    .string({
      required_error: MERGE_REFUSAL.chooseDuplicate,
      invalid_type_error: MERGE_REFUSAL.chooseDuplicate,
    })
    .trim()
    .min(1, MERGE_REFUSAL.chooseDuplicate),
});

/** `GET /doctors` and `GET /patients`: the envelope every list is wrapped in. */
export const DoctorPageSchema = pageOf(DoctorSchema);
export const PatientPageSchema = pageOf(PatientSchema);

/**
 * A patient's job orders with ONE doctor, the deleted left out: how many, how
 * many of them not dispatched and not archived, and when the newest of them
 * was booked.
 */
export const WithDoctorSchema = z.object({
  caseCount: count,
  activeCaseCount: count,
  lastCaseAt: InstantSchema.nullable(),
});

/**
 * A patient as a doctor's list carries one: the patient's record and, beside
 * it, `withDoctor`, counted over that patient's cases with this doctor.
 */
export const DoctorPatientSchema = PatientSchema.extend({ withDoctor: WithDoctorSchema });

/**
 * `GET /doctors/{id}/patients`: the patients with a job order under that
 * doctor, the deleted left out. Always the four keys of a page. With neither
 * `page` nor `pageSize` sent it holds every match at once, as it did before it
 * was paged: `page` is then 1 and `pageSize` the number of matches, or 1 when
 * there are none, so it is not held to the two hundred of a list.
 */
export const DoctorPatientsSchema = z.object({
  items: z.array(DoctorPatientSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});

/** `GET /doctors/summary`: the counts above the list of doctors, on the lab's calendar. */
export const DoctorSummarySchema = z.object({
  total: count,
  newThisWeek: count,
  newThisMonth: count,
  returning: count,
  withActiveCases: count,
  needsReview: count,
  oldFormat: count,
});

/** `GET /patients/summary`: the counts above the list of patients. */
export const PatientSummarySchema = z.object({
  total: count,
  newThisWeek: count,
  newThisMonth: count,
  withActiveCases: count,
});

/** What would happen, or did, to one doctor's old telephone text. */
export const PhoneConversionSchema = z.object({
  before: z.string(),
  outcome: z.enum(CONVERSION_OUTCOMES),
  /** The number as E.164 when it converts, else null. */
  after: z.string().nullable(),
  afterDisplay: z.string().nullable(),
  /** Why it is left for review, else null. */
  reason: z.string().nullable(),
});

/**
 * What would happen, or did, to one doctor's old address text. `after` is the
 * address it becomes; or, left for review, as much of it as is certain; or
 * null when not even the country is.
 */
export const AddressConversionSchema = z.object({
  before: z.string(),
  outcome: z.enum(CONVERSION_OUTCOMES),
  after: DoctorAddressSchema.nullable(),
  reason: z.string().nullable(),
});

/** One doctor with something waiting to be converted. A field with nothing waiting is null. */
export const ConversionItemSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  clinic: z.string().nullable(),
  phone: PhoneConversionSchema.nullable(),
  address: AddressConversionSchema.nullable(),
});

/**
 * `GET` and `POST /doctors/contact-conversion`: what converting the old text
 * would do, and what it did. Only the doctors with something waiting, by name
 * and then by id.
 */
export const ContactConversionSchema = z.object({
  counts: z.object({
    doctors: count,
    phonesConverted: count,
    phonesForReview: count,
    addressesConverted: count,
    addressesForReview: count,
  }),
  items: z.array(ConversionItemSchema),
});

/**
 * `GET /doctors/{id}/similar`: the other doctors who share this doctor's loose
 * key, which are its possible duplicates, by name and then by id. Not paged,
 * and empty for most.
 */
export const SimilarDoctorsSchema = z.object({ items: z.array(DoctorSchema) });

/**
 * `GET /doctors/{id}/cases` and `GET /patients/{id}/cases`: a page of ordinary
 * job orders, newest first, the dispatched and the archived among them and the
 * deleted left out. Money and identity follow the caller, as on any job order.
 */
export const ClientCasesPageSchema = pageOf(JobOrderSchema);

export type DoctorPayload = z.infer<typeof DoctorSchema>;
export type DoctorPatientPayload = z.infer<typeof DoctorPatientSchema>;
export type DoctorSummaryPayload = z.infer<typeof DoctorSummarySchema>;
export type PatientSummaryPayload = z.infer<typeof PatientSummarySchema>;
export type ContactConversionPayload = z.infer<typeof ContactConversionSchema>;
export type PatientDoctorPayload = z.infer<typeof PatientDoctorSchema>;
export type PatientPayload = z.infer<typeof PatientSchema>;
export type NewDoctorCommand = z.infer<typeof NewDoctorSchema>;
export type UpdateDoctorCommand = z.infer<typeof UpdateDoctorSchema>;
export type NewPatientCommand = z.infer<typeof NewPatientSchema>;
export type RenamePatientCommand = z.infer<typeof RenamePatientSchema>;
export type MergeDoctorCommand = z.infer<typeof MergeDoctorSchema>;
